import { useState } from "react";
import { motion } from "framer-motion";
import { Activity, Moon, Sun, Flame } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

function computeVoltage(sleep, stress, circadian) {
  return -30 - sleep * 1.5 - (10 - stress) * 1.5 - circadian * 1.0;
}

function stateFor(v) {
  if (v > -45) return { label: "System Depleted", tone: "text-red-400/90", note: "Chronic sympathetic overload. Burnout trajectory active." };
  if (v > -58) return { label: "Strained Output", tone: "text-copper-hover", note: "Performance is financed by debt. Recovery deficit compounding." };
  return { label: "Peak Cellular Voltage", tone: "text-gold", note: "Frictionless high performance. Mitochondrial output saturated." };
}

function Slider({ icon: Icon, label, value, setValue, testId, low, high }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label htmlFor={testId} className="flex items-center gap-2.5 text-[11px] uppercase tracking-[0.22em] text-neutral-400">
          <Icon size={14} className="text-gold/70" strokeWidth={1.5} />
          {label}
        </label>
        <span className="font-serif text-lg text-gold tabular-nums">{value}</span>
      </div>
      <input
        id={testId}
        data-testid={testId}
        type="range"
        min="0"
        max="10"
        step="1"
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="lux-range w-full"
      />
      <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] text-neutral-600">
        <span>{low}</span>
        <span>{high}</span>
      </div>
    </div>
  );
}

export default function VoltageMatrix() {
  const [sleep, setSleep] = useState(4);
  const [stress, setStress] = useState(7);
  const [circadian, setCircadian] = useState(3);

  const voltage = computeVoltage(sleep, stress, circadian);
  const pct = Math.min(100, Math.max(0, ((Math.abs(voltage) - 30) / 40) * 100));
  const state = stateFor(voltage);

  return (
    <section id="voltage" className="relative bg-ink-2 py-24 sm:py-32 lg:py-36 border-y border-gold/10" data-testid="voltage-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <p className="text-[11px] uppercase tracking-[0.35em] text-copper-hover mb-5" data-testid="voltage-eyebrow">
            Interactive Diagnostic
          </p>
          <h2 className="font-serif text-3xl sm:text-5xl text-gold font-medium tracking-tight leading-[1.15] mb-6" data-testid="voltage-heading">
            The Cellular Voltage Matrix
          </h2>
          <p className="text-sm sm:text-base text-neutral-400 font-light leading-relaxed max-w-lg">
            Your biology keeps an exact ledger. Adjust the three governing
            inputs of executive performance and watch your cellular membrane
            potential respond in real time — from burnout state to the -70mV
            optimization frontier.
          </p>
          <div className="gold-divider mt-8 max-w-xs" />
          <p className="text-xs uppercase tracking-[0.22em] text-neutral-600 mt-6">
            Burnout −30mV &nbsp;&nbsp;→&nbsp;&nbsp; High Performance −70mV
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="bg-ink-3 border border-[#26262A] p-7 sm:p-10 space-y-8"
          data-testid="voltage-panel"
        >
          <div className="flex items-end justify-between border-b border-gold/15 pb-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-neutral-500 mb-2">Membrane Potential</p>
              <motion.p
                key={voltage}
                initial={{ opacity: 0.35 }}
                animate={{ opacity: 1 }}
                className="font-serif text-5xl sm:text-6xl text-white leading-none tabular-nums"
                data-testid="voltage-value"
              >
                {voltage.toFixed(1)}
                <span className="text-xl sm:text-2xl text-gold ml-1.5">mV</span>
              </motion.p>
            </div>
            <div className="text-right">
              <p className={`text-[11px] uppercase tracking-[0.24em] font-semibold ${state.tone}`} data-testid="voltage-state">
                {state.label}
              </p>
            </div>
          </div>

          <div className="h-1 bg-[#26262A] w-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-copper to-gold"
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              data-testid="voltage-bar"
            />
          </div>

          <Slider icon={Moon} label="Sleep Architecture" value={sleep} setValue={setSleep} testId="voltage-sleep-slider" low="Fragmented" high="Deep / Glymphatic" />
          <Slider icon={Flame} label="Stress Load" value={stress} setValue={setStress} testId="voltage-stress-slider" low="Regulated" high="Sympathetic Overload" />
          <Slider icon={Sun} label="Circadian Alignment" value={circadian} setValue={setCircadian} testId="voltage-circadian-slider" low="Chaotic" high="Synchronized" />

          <p className="text-xs text-neutral-500 font-light leading-relaxed flex items-start gap-2.5 pt-2" data-testid="voltage-note">
            <Activity size={14} className="text-copper shrink-0 mt-0.5" strokeWidth={1.5} />
            {state.note}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
